import { Button, Card, Dialog, DialogContent, DialogContentText, DialogTitle, Grid } from '@material-ui/core'
import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { useState } from 'react'
import {
  NumberField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useNotify,
  usePermissions,
} from 'react-admin'
import { Form } from 'react-final-form'
import { Controller, useForm } from 'react-hook-form'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'

import { captureExceptionIfProduction } from 'helpers/utils'
import FeasibilityStatButton from '../../components/Common/FeasibilityStatButton'
import { hasPermission, Permissions } from '../../Permissions'
import Aside from '../Aside/aside'
import { LoadStops } from '../Common/Loads'
import { CustomLinkifiedField } from '../Fields'

interface TenderRejectionValues {
  reason_code: string
  description: string
}

const RejectTenderButton = (props: any) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const notify = useNotify()
  const { handleSubmit, control } = useForm<TenderRejectionValues>({
    defaultValues: {
      reason_code: 'NCL',
    },
    mode: 'onSubmit',
  })

  const sendRejection = (values: any) => {
    const reasonCode = values.reason_code
    const description = reasonCodes.find((elem) => elem.value === reasonCode)?.label
    axios
      .post(`/api/v1/console/sfms/tender-reply/${props.data.id}/reject/`, {
        reason_code: reasonCode,
        description,
      })
      .then((response) => {
        if (response.status === HttpStatus.NO_CONTENT) {
          notify('Tender rejected!', { type: 'success' })
        } else {
          throw response
        }
      })
      .catch((error) => {
        notify(`${error?.response?.data?.error || error}`, { type: 'warning' })
        captureExceptionIfProduction(error)
      })
  }

  const reasonCodes = [
    { label: 'Not Committed Lane', value: 'NCL' },
    { label: 'Constraint at DC', value: 'CAD' },
    { label: 'Constraint at Rail', value: 'CAR' },
    { label: 'Constraint at Vendor', value: 'CAV' },
    { label: 'Over Tender/Capacity', value: 'CMC' },
    { label: 'Capacity Type', value: 'CPT' },
    { label: 'Capacity Unavailable', value: 'CPU' },
    { label: 'Demand Exceeds Commitment (Surge)', value: 'DEC' },
    { label: 'Length of Haul', value: 'LNH' },
    { label: 'Multi Stop Load', value: 'MSL' },
    { label: 'Short Lead Time', value: 'SLT' },
  ]

  return (
    <div>
      <Button type="button" variant="contained" color="primary" onClick={() => setIsDialogOpen(true)}>
        Reject Tender
      </Button>
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Reject Tender</DialogTitle>
        <Form
          onSubmit={handleSubmit(sendRejection)}
          render={(formProps) => {
            return (
              <form>
                <DialogContent style={{ height: '30vh' }}>
                  <DialogContentText>Select reason and submit the rejection file.</DialogContentText>
                  <Controller
                    name="reason_code"
                    control={control}
                    render={({ field }) => (
                      <Dropdown {...field} staticLabel="Reason" options={reasonCodes} label="Reason" />
                    )}
                  />
                  <Button type="submit" variant="contained" color="primary" onClick={formProps.handleSubmit}>
                    Submit
                  </Button>
                </DialogContent>
              </form>
            )
          }}
        />
      </Dialog>
    </div>
  )
}

const AcceptTenderButton = (props: any) => {
  const notify = useNotify()
  const sendAccept = () => {
    axios
      .post(`/api/v1/console/sfms/tender-reply/${props.data.id}/accept/`, {})
      .then((response) => {
        if (response.status === HttpStatus.NO_CONTENT) {
          notify('Tender accepted!', { type: 'success' })
        } else {
          throw response
        }
      })
      .catch((error) => {
        notify(`${error?.response?.data?.error || error}`, { type: 'warning' })
        captureExceptionIfProduction(error)
      })
  }

  return (
    <Button type="button" variant="contained" color="primary" onClick={() => sendAccept()}>
      Accept Tender
    </Button>
  )
}

const EDILoadShowActions = (props: any) => {
  const { permissions } = usePermissions()
  const showTenderReplyButtons = permissions && hasPermission(permissions, Permissions.CanChangeEDILoad)
  const canChangeFeasibilityStat = hasPermission(permissions, Permissions.CanChangeFeasibilityStat)
  return showTenderReplyButtons ? (
    <TopToolbar>
      <Grid container direction="row" spacing={2} justifyContent="flex-start">
        {showTenderReplyButtons && (
          <Grid item>
            <AcceptTenderButton {...props} />
          </Grid>
        )}
        {showTenderReplyButtons && (
          <Grid item>
            <RejectTenderButton {...props} />
          </Grid>
        )}

        {canChangeFeasibilityStat && (
          <Grid item>
            <FeasibilityStatButton kind="edi_load" id={props.record.id} label="Start Feasibility Check" />
          </Grid>
        )}
      </Grid>
    </TopToolbar>
  ) : null
}

export const EDILoadShow = (props: any) => {
  return (
    <Show aside={<Aside />} actions={<EDILoadShowActions />} {...props}>
      <Grid container direction="column" spacing={3} alignItems="baseline" justifyContent="flex-start">
        <Grid item>
          <Card style={{ marginTop: '20px', paddingBottom: '20px' }}>
            <TabbedShowLayout record={props.record}>
              <Tab label="Load information">
                <TextField source="id" />
                <TextField source="integration_source" />
                <TextField source="source_id" label="Source Id" />
                <TextField source="source_human_id" label="Broker load ID" />
                <TextField source="contact.name" label="Contact" />
                <TextField source="contact.phone" label="Contact Phone" />
                <TextField source="contact.email" label="Contact Email" />
                <TextField source="company_name" />
                <CustomLinkifiedField source="internal_notes" label="Internal notes" record={props.record} />
                <LoadStops source="load_stops" record={props.record} />
                <TextField source="trip_rate" />
                <NumberField source="trip_distance_mi" />
              </Tab>
              <Tab label="Equipment">
                <TextField source="equipment" />
                <TextField source="equipment_detail" />
                <TextField source="truck_full_load" />
                <NumberField source="truck_weight_lb" />
                <NumberField source="truck_length_ft" />
              </Tab>
              <Tab label="Misc" path="misc">
                <NumberField source="trip_stops" />
                <TextField source="comment" />
                <TextField source="origin_location" />
                <TextField source="dest_location" />
              </Tab>
            </TabbedShowLayout>
          </Card>
        </Grid>
      </Grid>
    </Show>
  )
}
